import { useState, useCallback } from 'react';
import { empresasService } from '../services/api/empresas.service';
import { suscripcionesService } from '../services/api/suscripciones.service';
import type { Empresa } from '../modelos/Auth';
import type { CreateEmpresaDTO, UpdateEmpresaDTO } from '../services/api/empresas.service';
import { storageService } from '../utils/storage';
import { useWorkspace } from '../contexts/ContextoWorkspace';
import { registrarRegistroEmpresaExitoso } from '../shared/analitica/analitica';

export function useEmpresas() {
  const { refreshFromStorage } = useWorkspace();
  const [empresas, setEmpresas] = useState<Empresa[]>([]);
  const [empresa, setEmpresa] = useState<Empresa | null>(null);
  const [esCargando, setIsFetching] = useState(false);
  const [esGuardando, setIsSaving] = useState(false);
  const [esEliminando, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargarEmpresas = useCallback(
    async (search?: string) => {
      try {
        setIsFetching(true);
        setError(null);
        const response = await empresasService.getAll({ search });
        
        if (!response.ok || !response.data) {
          throw new Error('Error al cargar empresas');
        }

        // response.data es PaginatedResponse<Empresa> que tiene la propiedad data
        if (Array.isArray(response.data.data)) {
          setEmpresas(response.data.data);
          storageService.updateEmpresas(response.data.data);
        } else if (Array.isArray(response.data)) {
          setEmpresas(response.data);
          storageService.updateEmpresas(response.data);
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error desconocido al cargar empresas';
        setError(errorMessage);
      } finally {
        setIsFetching(false);
      }
    },
    []
  );

  const cargarEmpresa = useCallback(
    async (id: string): Promise<Empresa | null> => {
      try {
        setIsFetching(true);
        setError(null);
        const response = await empresasService.getById(id);
        
        if (!response.ok || !response.data) {
          throw new Error('Error al cargar la empresa');
        }

        setEmpresa(response.data);
        return response.data || null;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error al cargar empresa';
        setError(errorMessage);
        return null;
      } finally {
        setIsFetching(false);
      }
    },
    []
  );

  const crearEmpresa = useCallback(
    async (data: CreateEmpresaDTO): Promise<Empresa | null> => {
      try {
        setIsSaving(true);
        setError(null);

        const response = await empresasService.create(data);

        if (!response.ok || !response.data) {
          const errorMessage = response.mensaje || 'Error al crear empresa';
          throw new Error(errorMessage);
        }

        storageService.syncEntity('CREAR', 'empresas', response.data);
        setEmpresas(prev => [response.data!, ...prev]);
        void suscripcionesService.cargarLote([response.data.id]);
        registrarRegistroEmpresaExitoso({
          ruc: response.data.ruc,
          razonSocial: response.data.razonSocial,
          nombreComercial: response.data.nombreComercial,
        });

        if (response.data.establecimientoPrincipal) {
          storageService.syncEntity('CREAR', 'establecimientos', response.data.establecimientoPrincipal);
        }
        
        refreshFromStorage();
        return response.data || null;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error al crear empresa';
        setError(errorMessage);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [refreshFromStorage]
  );

  const actualizarEmpresa = useCallback(
    async (data: UpdateEmpresaDTO): Promise<Empresa | null> => {
      try {
        setIsSaving(true);
        setError(null);

        const response = await empresasService.update(data);

        if (!response.ok || !response.data) {
          const errorMessage = response.mensaje || 'Error al actualizar empresa';
          throw new Error(errorMessage);
        }

        storageService.syncEntity('ACTUALIZAR', 'empresas', response.data);
        setEmpresas(prev => prev.map(emp => 
          emp.id === response.data!.id ? response.data! : emp
        ));
        
        refreshFromStorage();
        return response.data || null;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error al actualizar empresa';
        setError(errorMessage);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [refreshFromStorage]
  );

  const eliminarEmpresa = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        setIsDeleting(true);
        setError(null);

        const response = await empresasService.delete(id);

        if (!response.ok) {
          throw new Error('Error al eliminar empresa');
        }

        storageService.syncEntity('ELIMINAR', 'empresas', { id });
        setEmpresas(prev => prev.filter(emp => emp.id !== id));
        
        refreshFromStorage();
        return true;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error al eliminar empresa';
        setError(errorMessage);
        return false;
      } finally {
        setIsDeleting(false);
      }
    },
    [refreshFromStorage]
  );

  const setPrincipal = useCallback(
    async (id: string): Promise<Empresa | null> => {
      try {
        setIsSaving(true);
        setError(null);

        const response = await empresasService.setPrincipal(id);

        if (!response.ok || !response.data) {
          throw new Error(response.mensaje || 'Error al establecer empresa principal');
        }

        const storedEmpresas = storageService.getEmpresas();
        const listaActualizada = storedEmpresas.map(emp =>
          emp.id === id ? response.data! : { ...emp, esPrincipal: false }
        );
        setEmpresas(listaActualizada);
        storageService.updateEmpresas(listaActualizada);
        refreshFromStorage();

        return response.data;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error al establecer principal';
        setError(errorMessage);
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [refreshFromStorage]
  );

  const toggleStatus = useCallback(
    async (id: string): Promise<Empresa | null> => {
      try {
        setIsSaving(true);
        setError(null);

        const response = await empresasService.toggleStatus(id);

        if (!response.ok || !response.data) {
          throw new Error('Error al cambiar estado de empresa');
        }

        storageService.syncEntity('ACTUALIZAR', 'empresas', response.data);
        setEmpresas(prev => prev.map(emp => 
          emp.id === response.data!.id ? response.data! : emp
        ));
        
        refreshFromStorage();
        return response.data;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Error al cambiar estado';
        setError(errorMessage);
        return null;
      } finally {
        setIsSaving(false);
      }
    },
    [refreshFromStorage]
  );

  return {
    empresas,
    empresa,
    esCargando,
    esGuardando,
    esEliminando,
    error,
    cargarEmpresas,
    cargarEmpresa,
    crearEmpresa,
    actualizarEmpresa,
    eliminarEmpresa,
    toggleStatus,
    setPrincipal,
  };
}
